'use client';

import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useLocale } from 'next-intl';
import { JSX, useEffect, useState } from 'react';
import { InputProps } from 'supertokens-auth-react/lib/build/recipe/emailpassword/components/library/input';

import { Button, HeroIcon } from '@/components/atoms';
import { Dropdown } from '@/components/molecules';
import { fetchSiteSalutations } from '@/lib/api';
import { useSiteBootstrap } from '@/lib/contexts/SiteBootstrapProvider';
import { useSiteConfig } from '@/lib/hooks/query';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils/tailwind';

export const SalutationSelector = ({
  value,
  onChange,
}: InputProps): JSX.Element | null => {
  const [open, setOpen] = useState(false);
  const [salutations, setSalutations] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { tenantId } = useSiteBootstrap();
  const siteQuery = useSiteConfig(tenantId);
  const site = siteQuery.data;
  const excludedLabels = ['Frau und Herr', 'Herr und Frau'];
  const locale = useLocale();

  useEffect(() => {
    const fetchSalutations = async () => {
      setLoading(true);
      try {
        const response = await fetchSiteSalutations(
          site?.configuration.tenant_id ?? '',
          locale,
        );
        const parsed = response
          .map(item => ({
            value: item.key.toString(),
            label: item.value,
          }))
          .filter(item => !excludedLabels.includes(item.label));

        if (parsed.length === 0) {
          setLoading(false);
          return;
        }

        setSalutations(parsed);

        if (!value && parsed.length > 0) {
          const frau = parsed.find(item => item.label === 'Frau');
          onChange(frau?.value ?? parsed[0].value);
        }
        setLoading(false);
      } catch (error) {
        onChange('error');
        setError(true);
        setLoading(false);
        const typedError = error as Error & { digest?: string };
        logger.error(typedError.message, {
          source: 'nextjs-app-salutationSelector',
          digest: typedError.digest,
          stack: typedError.stack,
        });
      }
    };

    fetchSalutations();
  }, [site?.configuration.tenant_id]);

  const selectedSalutation =
    salutations.find(s => s.value === value)?.label || salutations[0]?.label;

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    setOpen(false);
  };

  if (error || (!loading && salutations.length === 0)) return null;

  return (
    <div data-supertokens="inputContainer">
      <Dropdown
        open={open}
        onOpenChange={() => setOpen(!open)}
        data-supertokens="input"
      >
        <Dropdown.Trigger
          as={Button}
          variant="secondary"
          size="md"
          className="normal-case"
          data-supertokens="dropDownTrigger"
          isLoading={loading}
          icon={
            <HeroIcon
              icon={ChevronDownIcon}
              size="small"
              style={{
                width: '1.5rem',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
              className={cn(error && 'invisible')}
            />
          }
        >
          {selectedSalutation}
        </Dropdown.Trigger>
        {!error && (
          <Dropdown.Content
            align="start"
            className="z-3050 w-full backdrop-blur-3xl bg-(--drop-down-background-default) rounded-(--drop-down-radius)"
          >
            {salutations.map(({ value, label }) => (
              <Dropdown.Item
                key={value}
                data-supertokens="dropDownContent"
                onClick={() => handleSelect(value)}
                onKeyDown={() => handleSelect(value)}
                className={cn(
                  'rounded-(--drop-down-item-radius)',
                  'w-[350px]',
                  'text-(--drop-down-item-text-color-default)',
                  'bg-(--drop-down-item-background-color-default)',
                  'hover:text-(--drop-down-item-background-color-default)',
                  'hover:bg-(--drop-down-item-text-color-default)',
                  {
                    'bg-(--drop-down-item-background-color-selected)! text-(--drop-down-item-text-color-selected)!':
                      selectedSalutation === label,
                  },
                )}
                isActive={selectedSalutation === label}
              >
                {label}
              </Dropdown.Item>
            ))}
          </Dropdown.Content>
        )}
      </Dropdown>
    </div>
  );
};
